'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { createPortal } from 'react-dom'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { percorsoAttivo } from '@/lib/navigazione'
import { navigazione, site } from '@/lib/site'
import stili from './MobileNavigation.module.css'

const FOCUSABILI = 'a[href], button:not([disabled])'

export function MobileNavigation() {
  const percorso = usePathname() ?? '/'
  const [aperto, setAperto] = useState(false)
  const pannello = useRef<HTMLDivElement>(null)
  const apri = useRef<HTMLButtonElement>(null)
  const idPannello = useId()

  const chiudi = useCallback(() => {
    setAperto(false)
    apri.current?.focus()
  }, [])

  useEffect(() => {
    if (!aperto) return

    const scrollPrecedente = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    pannello.current?.querySelector<HTMLElement>(FOCUSABILI)?.focus()

    const suTasto = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.preventDefault()
        chiudi()
        return
      }
      if (evento.key !== 'Tab' || !pannello.current) return

      // Trappola del focus: il tabulatore resta dentro al pannello.
      const elementi = [...pannello.current.querySelectorAll<HTMLElement>(FOCUSABILI)]
      if (elementi.length === 0) return
      const primo = elementi[0]
      const ultimo = elementi[elementi.length - 1]

      if (evento.shiftKey && document.activeElement === primo) {
        evento.preventDefault()
        ultimo.focus()
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault()
        primo.focus()
      }
    }

    document.addEventListener('keydown', suTasto)
    return () => {
      document.removeEventListener('keydown', suTasto)
      document.body.style.overflow = scrollPrecedente
    }
  }, [aperto, chiudi])

  return (
    <>
      <button
        ref={apri}
        type="button"
        className={stili.apri}
        aria-expanded={aperto}
        aria-controls={idPannello}
        aria-label="Apri il menu"
        onClick={() => setAperto(true)}
      >
        <span className={stili.tratti} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>

      {/* Il pannello vive in un portale: l'intestazione usa backdrop-filter e
          creerebbe un blocco contenitore per gli elementi in position: fixed. */}
      {aperto &&
        createPortal(
          <>
          <div className={stili.velo} onClick={chiudi} aria-hidden="true" />
          <div
            ref={pannello}
            id={idPannello}
            className={stili.pannello}
            role="dialog"
            aria-modal="true"
            aria-label="Menu di navigazione"
          >
            <div className={stili.testata}>
              <span className={stili.titolo}>{site.nome}</span>
              <button type="button" className={stili.chiudi} onClick={chiudi} aria-label="Chiudi il menu">
                ×
              </button>
            </div>

            <nav className={stili.voci} aria-label="Navigazione principale">
              {navigazione.map(({ href, etichetta }) => {
                const attivo = percorsoAttivo(percorso, href)
                return (
                  <Link
                    key={href}
                    href={href}
                    className={`${stili.voce} ${attivo ? stili.voceAttiva : ''}`}
                    aria-current={attivo ? 'page' : undefined}
                    onClick={chiudi}
                  >
                    {etichetta}
                  </Link>
                )
              })}
            </nav>

            <p className={stili.piede}>
              <span>{site.claim}</span>
              <Link href="/contatti" onClick={chiudi}>
                Scrivimi
              </Link>
            </p>
          </div>
          </>,
          document.body,
        )}
    </>
  )
}
